'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { redirect } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { ClientInferResponses, contract, UserManagementApi } from '@pie/user-management-sdk';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import { useAuthContext } from './AuthContext';
import { useFeature, useFeatureContext } from './FeatureContext';

const sdk = new UserManagementApi({
  environment: env.NEXT_PUBLIC_PIE_ENV,
});

type Me = Extract<
  ClientInferResponses<typeof contract.getMe>,
  { status: 200 }
>['body']['data']['user'];

interface IdentityContextValues {
  user: Me;
}

export const IdentityContext = createContext<IdentityContextValues>({} as IdentityContextValues);

interface IdentityProviderProps {
  children: ReactNode;
  fallback: ReactNode;
}

export const IdentityProvider = ({ children, fallback }: IdentityProviderProps) => {
  const { getSessionHeaders } = useAuthContext();
  const featureContext = useFeatureContext();
  const isLive = useFeature('feature-upms-live', false);

  const { data, status } = useQuery({
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();

      const response = await sdk.client.getMe({
        extraHeaders: { authorization },
      });

      if (response.status !== 200) {
        throw new ApiError(response.status, response.body, response.headers);
      }

      return response.body.data;
    },
    queryKey: ['userManagement', 'getMe'],
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (
      featureContext.providerStatus === 'READY' &&
      featureContext.context === undefined &&
      status === 'success'
    ) {
      featureContext.setContext({
        email: data.user.email,
        userId: data.user.userId,
      });
    }
  }, [data, featureContext, status]);

  if (status === 'error') {
    return null;
  }

  // Wait for feature context so useFeature evaluates with the correct user, but don't wait
  // if the provider fails.
  const waitingForFeatureContext =
    (featureContext.context === undefined ||
      (featureContext.context !== undefined && featureContext.providerStatus !== 'READY')) &&
    featureContext.providerStatus !== 'ERROR' &&
    featureContext.providerStatus !== 'FATAL';

  if (status === 'pending' || waitingForFeatureContext) {
    return fallback;
  }

  if (isLive === false) {
    redirect(`https://${env.NEXT_PUBLIC_LEGACY_DOMAIN}`);
  }

  return <IdentityContext value={data}>{children}</IdentityContext>;
};

export const useIdentityContext = () => {
  const ctx = useContext(IdentityContext);
  if (!ctx) throw new Error('useIdentityContext must be used within <IdentityProvider>');
  return ctx;
};

/*
 * Derived
 */

export const fullName = ({ givenName, familyName }: Pick<Me, 'givenName' | 'familyName'>) => {
  return [givenName, familyName].filter(Boolean).join(' ');
};
